// TransactionStatusButton.jsx
import React, { useState, useRef, useEffect, useMemo } from "react";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";

const ALL_STATUSES = ["pending", "validated", "completed", "cancelled"];

const STATUS_LABELS = {
  pending: "En attente",
  validated: "Validée",
  completed: "Terminée",
  cancelled: "Annulée",
};

const ENDPOINTS = {
  validated: "validate",
  completed: "pay",
  cancelled: "cancel",
};

function btnColor(status) {
  switch (status) {
    case "pending":
      return "bg-yellow-600 hover:bg-yellow-700";
    case "validated":
      return "bg-blue-600 hover:bg-blue-700";
    case "completed":
      return "bg-green-600 hover:bg-green-700";
    case "cancelled":
      return "bg-red-600 hover:bg-red-700";
    default:
      return "bg-gray-600 hover:bg-gray-700";
  }
}

/**
 * ✅ Base API resolver:
 * - Prefer REACT_APP_API_URL if provided (recommended in prod)
 * - Otherwise fall back to same-origin (no localhost dependency)
 */
function getApiBase() {
  const env = (process.env.REACT_APP_API_URL || "").trim();
  if (env) return env.replace(/\/+$/, "");
  // same-origin fallback (works if frontend served by same domain/proxy)
  return window.location.origin.replace(/\/+$/, "");
}

/** fetch timeout helper (prevents "hang" forever in UI) */
async function fetchWithTimeout(url, options, timeoutMs = 20000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetchWithAuth(url, {
      ...options,
      signal: controller.signal,
    });
    return res;
  } finally {
    clearTimeout(id);
  }
}

export default function TransactionStatusButton({ transaction, onStatusChange }) {
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const base = useMemo(() => getApiBase(), []);
  const currentStatus = String(transaction?.status || "pending").toLowerCase();
  const label = STATUS_LABELS[currentStatus] || currentStatus;

  const handleChange = async (newStatus) => {
    newStatus = String(newStatus || "").toLowerCase();
    if (!newStatus || newStatus === currentStatus) return;
    if (loading) return; // ✅ hard guard

    const endpointName = ENDPOINTS[newStatus];
    if (!endpointName) {
      alert("Action non disponible pour ce statut.");
      return;
    }

    setLoading(true);
    setIsOpen(false);

    try {
      const endpoint = `${base}/api/admin/transactions/${transaction.id}/${endpointName}`;

      // ✅ IMPORTANT:
      // Your backend routes are:
      // - PATCH /transactions/:id/validate  (no body needed)
      // - PATCH /transactions/:id/pay       (needs { action: "complete", adminComment })
      // - PATCH /transactions/:id/cancel    (no body needed)
      const body =
        newStatus === "completed"
          ? JSON.stringify({ action: "complete", adminComment: "" })
          : undefined;

      const res = await fetchWithTimeout(
        endpoint,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body,
        },
        20000
      );

      // parse response safely
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          json.message ||
            json.error ||
            `Erreur lors de la mise à jour (HTTP ${res.status})`
        );
      }

      const updatedTx = json.transaction || json.updatedTransaction || json;

      // ✅ Update parent immediately
      if (updatedTx && updatedTx.id != null) {
        onStatusChange?.(transaction.id, updatedTx);
      } else {
        onStatusChange?.(transaction.id, { status: newStatus });
      }
    } catch (err) {
      console.error(err);

      if (err?.name === "AbortError") {
        alert("⏱️ La requête a expiré. Réessayez dans quelques secondes.");
      } else {
        alert(err?.message || "Erreur réseau");
      }
    } finally {
      setLoading(false);
    }
  };

  const availableStatuses = ALL_STATUSES.filter((s) => s !== currentStatus);

  return (
    <div className="relative inline-block text-left" ref={ref}>
      <button
        disabled={loading}
        onClick={() => setIsOpen((v) => !v)}
        className={`text-white px-3 py-2 rounded-md text-xs font-semibold ${btnColor(
          currentStatus
        )} disabled:opacity-60`}
        title="Changer le statut"
      >
        {loading ? "..." : label}
        <span className="ml-2 text-[10px]">▼</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-44 rounded-md shadow-lg bg-white dark:bg-gray-700 z-50 border border-gray-200 dark:border-gray-600">
          <ul className="py-1">
            {availableStatuses.map((status) => (
              <li
                key={status}
                onClick={() => handleChange(status)}
                className="cursor-pointer px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-white text-gray-900 text-sm"
              >
                {STATUS_LABELS[status] || status}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
